import { SwalService } from './../../../../core/services/utils/swal.service';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  LangChangeEvent,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { Subject as RxJsSubject, forkJoin, of } from 'rxjs';
import { CustomTabComponent } from '../../../ui/custom-tab/custom-tab.component';
import {
  SearchableSelectComponent,
  SelectItem,
} from '../../../ui/searchable-select/searchable-select.component';
import { FilterBarComponent } from '../../../ui/filter-bar/filter-bar.component';
import { ButtonComponent } from '../../../ui/button/button.component';
import { SyncfusionL10nService } from '../../../../shared/utils/syncfusion-l10n.service';
import { LanguageService } from '../../../../core/services/utils/language.service';
import { AddProfessorComponent } from '../../../../shared/modals/admin/add-professor/add-professor.component';
import { ModalComponent } from '../../../ui/modal/modal.component';

import { LearningService } from '../../../../core/services/learning-path.service';
import { LearningPath } from '../../../../core/interfaces/learning-path';
import { Subject } from '../../../../core/interfaces/subject';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  CreateProfessorDto,
  DeleteProfessorDto,
  Professor,
  ProfessorAssignment,
  UpdateProfessorDto,
} from '../../../../core/interfaces/professor';
import { ProfessorService } from '../../../../core/services/professor.service';
import { AuthService } from '../../../../core/services/utils/auth.service';
import { matchesSearch } from '../../../../core/utils/search-text';

/** Professeur enrichi des matières qu'il enseigne, pour l'affichage en table. */
interface ProfessorRow extends Professor {
  /** « Maths, Physique » — matières affectées, tous parcours confondus. */
  subjectsLabel: string;
}

@Component({
  selector: 'app-professors',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    CustomTabComponent,
    SearchableSelectComponent,
    FilterBarComponent,
    ButtonComponent,
    ModalComponent,
    FormsModule,
    AddProfessorComponent,
  ],
  templateUrl: './professors.component.html',
  styleUrl: './professors.component.css',
})
export class ProfessorsComponent implements OnInit, OnDestroy {
  /**
   * Droit de modifier le référentiel des professeurs.
   *
   * <p>L'écran s'ouvre en lecture à qui porte `professors.read` — l'inspecteur
   * en fait partie. Les verbes d'écriture demandent `professors.write`, que le
   * back revérifie : ce test n'est pas la protection, c'est l'honnêteté de
   * l'écran.</p>
   */
  readonly canWrite = inject(AuthService).hasPermission('professors.write');

  private translate = inject(TranslateService);
  private syncfusionL10n = inject(SyncfusionL10nService);
  private languageService = inject(LanguageService);
  private readonly learningService = inject(LearningService);
  private readonly professorService = inject(ProfessorService);
  private readonly swalService = inject(SwalService);
  private destroy$ = new RxJsSubject<void>();

  /**
   * Tous les professeurs, filtrés ensuite côté client. La liste est chargée en
   * une fois (l'API renvoie tout quand aucune matière n'est passée) : c'est ce
   * qui rend possible « tous les professeurs d'un parcours », que l'API ne sait
   * pas filtrer elle-même.
   */
  professors: Professor[] = [];

  /**
   * Affectations parcours·matière de tous les professeurs. Seule source qui
   * relie un professeur à un parcours — le DTO professeur ne porte pas cette
   * information.
   */
  assignments: ProfessorAssignment[] = [];

  learningPaths: LearningPath[] = [];

  /** Facettes actives. `null` = pas de filtre sur cette facette. */
  selectedPathId: number | null = null;
  selectedSubjectId: number | null = null;
  search = '';

  /**
   * La table n'a pas de barre d'outils ici : sa recherche intégrée doublait
   * celle de la barre de filtres, sans être comptée ni remise à zéro par elle.
   */
  readonly tableToolbar: string[] = [];

  /**
   * Listes dérivées **matérialisées** plutôt qu'exposées via des accesseurs :
   * un getter qui reconstruit un tableau serait ré-exécuté à chaque cycle de
   * détection, et la table le verrait changer d'identité en permanence.
   */
  pathOptions: SelectItem[] = [];
  subjectOptions: SelectItem[] = [];
  visibleProfessors: ProfessorRow[] = [];

  /** Affectations indexées par professeur : le filtrage reste linéaire. */
  private assignmentsByProfessor = new Map<string, ProfessorAssignment[]>();

  selectedProfessor: Professor | null = null;
  isRTL = false;
  currentLocale = 'ar';
  isLoading = false;
  isLoadingPaths = false;
  isSaving = false;
  isEditMode = false;
  error: string | null = null;
  /** Id du professeur dont le compte est en cours de création (désactive son bouton). */
  creatingAccountFor: string | null = null;

  translatedHeaders = {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    //tel: '',
    grade: '',
    specialization: '',
    subjects: '',
    status: '',
    actions: '',
  };

  professorColumns: Array<{
    field: string;
    headerText: string;
    width: number;
    textAlign?: string;
    template?: string;
    freeze?: string;
  }> = [];

  showProfessorModal = false;

  ngOnInit(): void {
    this.initializeComponent();
    this.subscribeToLanguageChanges();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    this.currentLocale = this.languageService.getCurrentLang();
    this.isRTL = !this.languageService.isLTR();
    this.loadTranslations();
  }

  private subscribeToLanguageChanges(): void {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: LangChangeEvent) => {
        this.currentLocale = event.lang;
        this.isRTL = !this.languageService.isLTR();
        this.loadTranslations();
      });
  }

  /* ── Chargement ──────────────────────────────────────────── */

  /**
   * Référentiel (parcours + matières) et professeurs en un seul aller-retour
   * chacun. Un échec du référentiel ne doit pas priver l'écran de sa liste :
   * chaque source est neutralisée séparément.
   */
  loadData(): void {
    this.isLoading = true;
    this.isLoadingPaths = true;
    this.error = null;

    forkJoin({
      paths: this.learningService.getLearningPaths(true, false).pipe(
        catchError((err) => {
          console.error('Error loading learning paths:', err);
          this.swalService.warning(
            this.translate.instant('ADMIN.ERROR_LOADING_PATHS'),
          );
          return of(null);
        }),
      ),
      professors: this.professorService.getProfessors().pipe(
        catchError((err) => {
          console.error('Error loading professors:', err);
          this.error = this.translate.instant('ADMIN.ERROR_LOADING_PROFESSORS');
          this.swalService.warning(this.error!);
          return of(null);
        }),
      ),
      // Les affectations n'alimentent que le filtre par parcours et la colonne
      // « matières » : leur absence dégrade l'écran, elle ne le casse pas.
      assignments: this.professorService.getAllAssignments().pipe(
        catchError((err) => {
          console.error('Error loading professor assignments:', err);
          return of(null);
        }),
      ),
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.isLoadingPaths = false;
        }),
      )
      .subscribe(({ paths, professors, assignments }) => {
        this.learningPaths = paths?.data ?? [];
        this.professors = professors?.data ?? [];
        this.setAssignments(assignments?.data ?? []);
        this.rebuildPathOptions();
        this.rebuildSubjectOptions();
        this.applyFilters();
      });
  }

  /** Recharge la liste après une écriture, en conservant les filtres courants. */
  loadProfessors(): void {
    this.isLoading = true;

    forkJoin({
      professors: this.professorService.getProfessors(),
      assignments: this.professorService
        .getAllAssignments()
        .pipe(catchError(() => of(null))),
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: ({ professors, assignments }) => {
          this.professors = professors?.data ?? [];
          if (assignments) this.setAssignments(assignments.data ?? []);
          this.applyFilters();
        },
        error: (error) => {
          console.error('Error loading professors:', error);
          this.swalService.warning(
            this.translate.instant('ADMIN.ERROR_LOADING_PROFESSORS'),
          );
        },
      });
  }

  loadTranslations(): void {
    this.translate
      .get([
        //'DATA_TABLE.ID',
        'DATA_TABLE.FIRST_NAME',
        'DATA_TABLE.LAST_NAME',
        'DATA_TABLE.EMAIL',
        //'DATA_TABLE.TELEPHONE',
        'DATA_TABLE.GRADE',
        'DATA_TABLE.SPECIALIZATION',
        'ADMIN.PROFESSOR_SUBJECTS',
        'ADMIN.STATUS',
        'DATA_TABLE.ACTIONS',
      ])
      .subscribe((translations) => {
        this.translatedHeaders = {
          id: translations['DATA_TABLE.ID'],
          firstName: translations['DATA_TABLE.FIRST_NAME'],
          lastName: translations['DATA_TABLE.LAST_NAME'],
          email: translations['DATA_TABLE.EMAIL'],
          //tel: translations['DATA_TABLE.TELEPHONE'],
          grade: translations['DATA_TABLE.GRADE'],
          specialization: translations['DATA_TABLE.SPECIALIZATION'],
          subjects: translations['ADMIN.PROFESSOR_SUBJECTS'],
          status: translations['ADMIN.STATUS'],
          actions: translations['DATA_TABLE.ACTIONS'],
        };
        this.buildColumns();
      });
  }

  buildColumns(): void {
    this.professorColumns = [
      {
        field: 'firstName',
        headerText: this.translatedHeaders.firstName,
        width: 150,
        textAlign: this.isRTL ? 'Right' : 'Left',
      },
      {
        field: 'lastName',
        headerText: this.translatedHeaders.lastName,
        width: 150,
        textAlign: this.isRTL ? 'Right' : 'Left',
      },
      {
        field: 'email',
        headerText: this.translatedHeaders.email,
        width: 200,
        textAlign: this.isRTL ? 'Right' : 'Left',
      },
      /*{
        field: 'tel',
        headerText: this.translatedHeaders.tel,
        width: 130,
        textAlign: this.isRTL ? 'Right' : 'Left',
      },*/
      {
        field: 'grade',
        headerText: this.translatedHeaders.grade,
        width: 150,
        textAlign: this.isRTL ? 'Right' : 'Left',
      },
      {
        field: 'specialization',
        headerText: this.translatedHeaders.specialization,
        width: 150,
        textAlign: this.isRTL ? 'Right' : 'Left',
      },
      // La liste pouvant désormais montrer tous les parcours à la fois, la
      // matière enseignée est nécessaire pour situer chaque professeur.
      {
        field: 'subjectsLabel',
        headerText: this.translatedHeaders.subjects,
        width: 200,
        textAlign: this.isRTL ? 'Right' : 'Left',
      },
      {
        field: 'id',
        headerText: '',
        width: 210,
        freeze: 'Right',
        textAlign: 'Center',
        template: 'actionTemplate',
      },
    ];
  }

  setGridLanguage(lang: string): void {
    this.currentLocale = lang;
  }

  /* ── Facettes ────────────────────────────────────────────── */

  private rebuildPathOptions(): void {
    this.pathOptions = this.learningPaths.map((p) => ({
      id: p.id,
      label: p.title,
    }));
  }

  /** Toutes les matières du référentiel, à plat, avec leur parcours d'origine. */
  private get allSubjects(): Subject[] {
    return this.learningPaths.flatMap((path) =>
      (path.subjects ?? []).map((s) => ({
        ...s,
        learningPathId: s.learningPathId ?? path.id,
      })),
    );
  }

  /**
   * Matières proposées : restreintes au parcours choisi quand il y en a un,
   * sinon toutes — même cascade que la facette « thème » de la banque de
   * questions.
   */
  private rebuildSubjectOptions(): void {
    const pathId = this.selectedPathId;
    const scoped =
      pathId == null
        ? this.allSubjects
        : this.allSubjects.filter((s) => s.learningPathId === pathId);
    this.subjectOptions = scoped.map((s) => ({ id: s.id, label: s.name }));
  }

  private setAssignments(assignments: ProfessorAssignment[]): void {
    this.assignments = assignments;
    this.assignmentsByProfessor = new Map();
    for (const assignment of assignments) {
      const key = String(assignment.professorId);
      const bucket = this.assignmentsByProfessor.get(key);
      if (bucket) bucket.push(assignment);
      else this.assignmentsByProfessor.set(key, [assignment]);
    }
  }

  get isLoadingSubjects(): boolean {
    return this.isLoadingPaths;
  }

  /** Nombre de facettes actives — pilote le compteur et la remise à zéro. */
  get activeFilterCount(): number {
    let n = 0;
    if (this.selectedPathId != null) n++;
    if (this.selectedSubjectId != null) n++;
    if (this.search.trim()) n++;
    return n;
  }

  onPathChange(pathId: number | null): void {
    this.selectedPathId = pathId;
    this.rebuildSubjectOptions();
    // La matière choisie n'appartient probablement plus au nouveau parcours :
    // la conserver produirait un croisement vide et incompréhensible.
    if (
      this.selectedSubjectId != null &&
      !this.subjectOptions.some((o) => o.id === this.selectedSubjectId)
    ) {
      this.selectedSubjectId = null;
    }
    this.applyFilters();
  }

  onSubjectChange(subjectId: number | null): void {
    this.selectedSubjectId = subjectId;
    this.applyFilters();
  }

  onSearchChange(text: string): void {
    this.search = text;
    this.applyFilters();
  }

  resetFilters(): void {
    this.selectedPathId = null;
    this.selectedSubjectId = null;
    this.search = '';
    this.rebuildSubjectOptions();
    this.applyFilters();
  }

  /* ── Liste filtrée ───────────────────────────────────────── */

  /** Affectations parcours·matière d'un professeur donné. */
  private assignmentsOf(professorId: string): ProfessorAssignment[] {
    return this.assignmentsByProfessor.get(String(professorId)) ?? [];
  }

  /** Professeurs après application des trois facettes, enrichis pour la table. */
  private applyFilters(): void {
    const pathId = this.selectedPathId;
    const subjectId = this.selectedSubjectId;

    this.visibleProfessors = this.professors
      .filter((p) => {
        if (pathId == null && subjectId == null) return true;
        const mine = this.assignmentsOf(p.id);
        // Une matière est toujours plus précise que son parcours : dès qu'elle
        // est choisie, elle seule décide.
        if (subjectId != null) {
          return mine.some((a) => Number(a.subjectId) === subjectId);
        }
        return mine.some((a) => Number(a.learningPathId) === pathId);
      })
      .map((p) => ({ ...p, subjectsLabel: this.subjectsLabelOf(p) }))
      .filter((p) =>
        matchesSearch(
          this.search,
          p.firstName,
          p.lastName,
          p.email,
          p.grade,
          p.specialization,
          p.subjectsLabel,
        ),
      );
  }

  /** Libellé des matières enseignées ; tiret cadratin si aucune affectation. */
  private subjectsLabelOf(professor: Professor): string {
    const names = Array.from(
      new Set(
        this.assignmentsOf(professor.id)
          .map((a) => a.subjectName)
          .filter((n): n is string => !!n),
      ),
    );
    return names.length ? names.join(', ') : '—';
  }

  handleRowClick(professor: Professor): void {
    console.log('Clicked professor:', professor);
  }

  addProfessor(): void {
    // Aucun pré-requis de filtre : le parcours et la matière se choisissent
    // dans le modal. Le bouton reste donc actif quel que soit l'état du filtre.
    this.selectedProfessor = null;
    this.isEditMode = false;
    this.showProfessorModal = true;
  }

  onEditProfessor(professor: Professor) {
    this.selectedProfessor = { ...professor };
    this.isEditMode = true;
    this.showProfessorModal = true;
  }

  onDeleteProfessor(professor: DeleteProfessorDto) {
    console.log('Deleting professor:', professor);

    // Show confirmation dialog using the new Promise-based confirm
    this.translate
      .get(['SWAL.DELETE_CONFIRM', 'SWAL.DELETE_CONFIRM_MESSAGE'])
      .pipe(takeUntil(this.destroy$))
      .subscribe((translations) => {
        this.swalService.confirm(
          translations['ADMIN.CONFIRM_DELETE_PROFESSOR_MESSAGE'],
          () => {
            this.deleteProfessor(professor.id);
          },
        );
      });
  }

  private deleteProfessor(professorId: number): void {
    this.professorService
      .deleteProfessor(professorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.translate
            .get('SWAL.DELETE_SUCCESS')
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.swalService.success(message);
              this.loadProfessors();
            });
        },
        error: (error) => {
          console.error('Error deleting professor:', error);
          this.translate
            .get('SWAL.ERROR')
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.swalService.error(message);
            });
        },
      });
  }

  /** Vrai tant que le professeur n'a pas de compte d'accès rattaché. */
  hasAccount(professor: Professor): boolean {
    return !!professor.userId;
  }

  onCreateAccount(professor: Professor): void {
    if (this.hasAccount(professor) || this.creatingAccountFor) return;

    this.swalService.confirm(
      this.translate.instant('ADMIN.CONFIRM_CREATE_ACCOUNT_MESSAGE', {
        email: professor.email,
      }),
      () => this.createAccount(professor),
    );
  }

  private createAccount(professor: Professor): void {
    this.creatingAccountFor = professor.id;

    this.professorService
      .createProfessorAccount(Number(professor.id))
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.creatingAccountFor = null)),
      )
      .subscribe({
        next: (response) => {
          // On recharge plutôt que de patcher la ligne : le back peut avoir
          // rattaché un compte préexistant au lieu d'en créer un nouveau.
          this.swalService.success(
            this.translate.instant('ADMIN.ACCOUNT_CREATED_SUCCESS', {
              email: response?.data?.email ?? professor.email,
            }),
          );
          this.loadProfessors();
        },
        error: (error) => {
          console.error('Error creating professor account:', error);
          this.swalService.error(
            error?.error?.message ??
              this.translate.instant('ADMIN.ERROR_CREATING_ACCOUNT'),
          );
        },
      });
  }

  onShowProfessor(professor: Professor) {
    this.selectedProfessor = { ...professor };
    this.isEditMode = false;

    const activeStatus = professor.isActive
      ? this.translate.instant('ADMIN.ACTIVE')
      : this.translate.instant('ADMIN.INACTIVE');

    const subjects =
      this.subjectsLabelOf(professor) === '—'
        ? this.translate.instant('ADMIN.NO_SUBJECT_ASSIGNED')
        : this.subjectsLabelOf(professor);

    this.swalService.info(
      `<div style="text-align: left;">
        <strong>${this.translate.instant('DATA_TABLE.FIRST_NAME')}:</strong> ${professor.firstName}<br>
        <strong>${this.translate.instant('DATA_TABLE.LAST_NAME')}:</strong> ${professor.lastName}<br>
        <strong>${this.translate.instant('DATA_TABLE.EMAIL')}:</strong> ${professor.email}<br>
        <strong>${this.translate.instant('DATA_TABLE.GRADE')}:</strong> ${professor.grade}<br>
        <strong>${this.translate.instant('DATA_TABLE.SPECIALIZATION')}:</strong> ${professor.specialization}<br>
        <strong>${this.translate.instant('ADMIN.PROFESSOR_SUBJECTS')}:</strong> ${subjects}<br>
        <strong>${this.translate.instant('ADMIN.STATUS')}:</strong> ${activeStatus}
      </div>`,
      `${professor.firstName} ${professor.lastName}`,
    );
  }

  handleProfessorSaved(
    professorData: CreateProfessorDto | UpdateProfessorDto,
  ): void {
    // L'affectation (matière) est portée par le DTO (subjectIds) et non
    // plus par le filtre : plus besoin d'exiger une matière sélectionnée.
    this.isSaving = true;

    // Check if professorData has an id property to determine if it's an update
    const isUpdate = 'id' in professorData && professorData.id !== undefined;

    const operation = isUpdate
      ? this.professorService.updateProfessor(
          (professorData as UpdateProfessorDto).id,
          professorData as UpdateProfessorDto,
        )
      : this.professorService.createProfessor(
          professorData as CreateProfessorDto,
        );

    operation
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
        next: () => {
          const messageKey = isUpdate
            ? 'ADMIN.PROFESSOR_UPDATED_SUCCESS'
            : 'ADMIN.PROFESSOR_CREATED_SUCCESS';

          this.translate
            .get(messageKey)
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.swalService.success(message);
              this.closeModal();
              // Un filtre actif pourrait masquer le professeur qu'on vient
              // de créer : on le lève pour qu'il soit visible. Une
              // modification, elle, part d'une ligne déjà affichée — lever les
              // filtres ferait perdre à l'administrateur l'endroit où il était.
              if (!isUpdate) this.resetFilters();
              this.loadProfessors();
            });
        },
        error: (error) => {
          console.error('Error saving professor:', error);
          const errorKey = isUpdate
            ? 'SWAL.ERROR'
            : 'ADMIN.ERROR_CREATING_PROFESSOR';

          this.translate
            .get(errorKey)
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.swalService.error(message);
            });
        },
      });
  }

  onModalClosed(): void {
    this.closeModal();
  }

  private closeModal(): void {
    this.showProfessorModal = false;
    this.selectedProfessor = null;
    this.isEditMode = false;
  }
}
